import { debounce } from 'lodash';
import { type SetStateAction, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Replaces usehooks-ts@3.1.1's hook of the same name, which builds two debounce instances and
 * cancels the one calls never went through — so a pending setState still fires after unmount.
 * It also rebuilds the debouncer on every render when the callback is an inline arrow. Keeping
 * the callback in a ref fixes both.
 */
export function useDebounceCallback<Args extends Array<unknown>>(
  func: (...args: Args) => void,
  delay = 500,
) {
  const funcRef = useRef(func);
  funcRef.current = func;

  const debounced = useMemo(
    () => debounce((...args: Args) => funcRef.current(...args), delay),
    [delay],
  );

  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
}

export function useDebounceValue<T>(value: T, delay = 500) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const previousRef = useRef(value);
  const update = useDebounceCallback<[SetStateAction<T>]>(setDebouncedValue, delay);

  // Upstream updates during render rather than in an effect; keeping that so call-site timing
  // does not shift.
  if (previousRef.current !== value) {
    update(value);
    previousRef.current = value;
  }

  return [debouncedValue, update] as const;
}
